import { combineReducers } from 'redux';
import { persistReducer } from 'redux-persist';
import config from './store/config/Reducer';
import adminData from './store/connect/userData/reducer';
import connectAppData from './store/connect/appData/reducer';
import connectUi from './store/connect/ui/reducer';
import connectCamera from './store/connect/camera/reducer';
import common from './store/appData/common/reducer';
import user from './store/userData/user/reducer';
import texts from './store/appData/texts/Reducer';
import carEquipments from './store/appData/carEquipments/Reducer';
import serviceTypes from './store/appData/serviceTypes/Reducer';
import rental from './store/userData/rental/reducer';
import bookings from './store/userData/bookings/reducer';
import carsSearch from './store/carsSearch/Reducer';
import bluetooth from './store/bluetooth/Reducer';
import device from './store/device/Reducer';
import networkStatus from './store/networkStatus/Reducer';
import notifications from './store/ui/notifications/Reducer';
import dimensions from './store/ui/dimensions/Reducer';
import map from './store/ui/map/Reducer';
import deeplink from './store/ui/deeplink/Reducer';
import firstSteps from './store/ui/firstSteps/Reducer';
import damages from './store/ui/damages/Reducer';
import services from './store/service/reducer';
import areas from './store/appData/areas/Reducer';
import pois from './store/appData/pois/Reducer';
import features from './store/appData/features/Reducer';
import camera from './store/camera/Reducer';
import pricing from './store/pricing/Reducer';
import { getPersistConfig } from './getPersistConfig';

export const RESET_STORE_ACTION = 'RESET_STORE';

const appReducer = combineReducers({
  connect: combineReducers({
    userData: persistReducer(getPersistConfig('adminData'), adminData),
    appData: persistReducer(getPersistConfig('connectAppData'), connectAppData),
    ui: persistReducer(getPersistConfig('connectUi'), connectUi),
    camera: persistReducer(getPersistConfig('connectCamera'), connectCamera),
  }),
  appData: persistReducer(
    getPersistConfig('appData'),
    combineReducers({
      features,
      areas,
      pois,
      texts,
      carEquipments,
      serviceTypes,
      common,
    })
  ),
  services,
  userData: combineReducers({
    user: persistReducer(getPersistConfig('user'), user),
    rental: persistReducer(getPersistConfig('rental'), rental),
    bookings: persistReducer(getPersistConfig('bookings'), bookings),
  }),
  carsSearch: persistReducer(getPersistConfig('carsSearch'), carsSearch),
  device: persistReducer(getPersistConfig('device'), device),
  bluetooth: persistReducer(getPersistConfig('bluetooth'), bluetooth),
  pricing,
  networkStatus: persistReducer(
    getPersistConfig('networkStatus'),
    networkStatus
  ),
  ui: combineReducers({
    notifications,
    dimensions,
    map: persistReducer(getPersistConfig('map'), map),
    deeplink,
    firstSteps: persistReducer(getPersistConfig('firstSteps'), firstSteps),
    damages: persistReducer(getPersistConfig('damages'), damages),
  }),
  camera: persistReducer(getPersistConfig('camera'), camera),
  config: persistReducer(getPersistConfig('config'), config),
});

// Helper to extract _persist keys from state tree
const extractPersistKeys = (state: any): any => {
  if (!state || typeof state !== 'object') return {};

  const result: any = {};

  Object.keys(state).forEach((key) => {
    if (key === '_persist') {
      result._persist = state._persist;
    } else if (typeof state[key] === 'object' && state[key] !== null) {
      const nested = extractPersistKeys(state[key]);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
    }
  });

  return result;
};

// Helper to merge _persist keys back into fresh state
const mergePersistKeys = (freshState: any, persistKeys: any): any => {
  if (!persistKeys || typeof persistKeys !== 'object') return freshState;
  if (!freshState || typeof freshState !== 'object') return freshState;

  const result = { ...freshState };

  Object.keys(persistKeys).forEach((key) => {
    if (key === '_persist') {
      result._persist = persistKeys._persist;
    } else if (result[key] && typeof result[key] === 'object') {
      result[key] = mergePersistKeys(result[key], persistKeys[key]);
    }
  });

  return result;
};

// Wrap the app reducer to handle RESET_STORE action
const rootReducer = (state: any, action: any) => {
  // When RESET_STORE is dispatched, reset state but preserve _persist metadata
  if (action.type === RESET_STORE_ACTION) {
    // Extract all _persist keys from current state
    const persistKeys = extractPersistKeys(state);
    // Get fresh initial state
    const freshState = appReducer(undefined, action);
    // Merge _persist keys back to avoid redux-persist warnings
    return mergePersistKeys(freshState, persistKeys);
  }
  return appReducer(state, action);
};

export default rootReducer;
